import { reaction } from "mobx";
import { observer } from "mobx-react-lite";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	aircraftStore,
	configurationStore,
	cwpStore,
	datablockStore,
	simulatorStore,
} from "../../state";
import {
	convertMetersToFlightLevel,
	convertNMToFeet,
	formatFeetCompact,
	formatSimulatorTimeHM,
} from "../../utils";
import TimelineEventCard from "./TimelineEventCard";
import TimelineSeparator from "./TimelineSeparator";
import {
	BOTTOM_PADDING_PX,
	EVENT_GAP_PX,
	EVENT_HEIGHT_PX,
	type PositionedEvent,
	SCALE_PRESETS,
	type ScalePreset,
	type TimelineEvent,
} from "./types";

/** Placeholder interval between conflicts when real time is unavailable (5 minutes in seconds) */
const PLACEHOLDER_CONFLICT_INTERVAL_SEC = 5 * 60;

/**
 * VerticalTimeline — Tailwind + DaisyUI
 *
 * - Creates a fixed-width vertical scheduler column that fills screen height.
 * - Renders events positioned by time relative to "now" (bottom of timeline).
 * - Zoomable via mouse wheel or range slider.
 * - Separator lines adapt to current scale.
 * - Events have fixed height and stack vertically to avoid overlaps.
 * - Events can be dragged up/down to adjust their time offset.
 */

/**
 * Determine separator interval based on scale
 * Returns interval in minutes
 */
function getSeparatorInterval(scaleMinutes: number): number {
	if (scaleMinutes <= 10) {
		return 1;
	}
	if (scaleMinutes <= 15) {
		return 3;
	}
	if (scaleMinutes <= 30) {
		return 5;
	}
	if (scaleMinutes <= 60) {
		return 10;
	}
	if (scaleMinutes <= 120) {
		return 15;
	}
	return 30;
}

/**
 * Stack events vertically to avoid overlaps.
 * Events are positioned from bottom (now) upward (future).
 * Returns events with adjusted bottom positions.
 */
function stackEvents(events: PositionedEvent[]): PositionedEvent[] {
	if (events.length === 0) {
		return [];
	}

	const getEventPriority = (event: PositionedEvent): number => {
		if (event.priority !== undefined) {
			return event.priority;
		}

		if (event.severity === "severe") {
			return 2;
		}

		if (event.severity === "potential") {
			return 1;
		}

		return 0;
	};

	const sortedByTime = events.toSorted((a, b) => a.bottomPx - b.bottomPx);
	const spacing = EVENT_HEIGHT_PX + EVENT_GAP_PX;
	const result: PositionedEvent[] = [];
	const currentGroup: PositionedEvent[] = [];

	let groupMaxTop = Number.NEGATIVE_INFINITY;
	let nextAvailableBottom = Number.NEGATIVE_INFINITY;

	const flushGroup = () => {
		if (currentGroup.length === 0) {
			return;
		}

		const groupBaseBottom = currentGroup[0].bottomPx;
		const stackedBaseBottom = Math.max(groupBaseBottom, nextAvailableBottom);

		const priorityOrderedGroup = currentGroup.toSorted((a, b) => {
			const priorityDelta = getEventPriority(b) - getEventPriority(a);
			if (priorityDelta !== 0) {
				return priorityDelta;
			}

			const timeDelta = a.bottomPx - b.bottomPx;
			if (timeDelta !== 0) {
				return timeDelta;
			}

			return a.id.localeCompare(b.id);
		});

		for (const [index, event] of priorityOrderedGroup.entries()) {
			result.push({ ...event, bottomPx: stackedBaseBottom + index * spacing });
		}

		nextAvailableBottom = stackedBaseBottom + currentGroup.length * spacing;
		currentGroup.length = 0;
	};

	for (const event of sortedByTime) {
		const eventTop = event.bottomPx + EVENT_HEIGHT_PX;

		if (currentGroup.length === 0) {
			currentGroup.push(event);
			groupMaxTop = eventTop;
			continue;
		}

		// Build transitive overlap groups from original time positions.
		if (event.bottomPx < groupMaxTop) {
			currentGroup.push(event);
			groupMaxTop = Math.max(groupMaxTop, eventTop);
			continue;
		}

		flushGroup();
		currentGroup.push(event);
		groupMaxTop = eventTop;
	}

	flushGroup();
	return result;
}

/**
 * Create a normalized key for an aircraft pair so (A,B) and (B,A) map to the same pair.
 */
function createAgendaPairKey(id1: string, id2: string): string {
	return id1 < id2 ? `${id1}:${id2}` : `${id2}:${id1}`;
}

/**
 * Rank MTCD conflict severity where higher number means more severe.
 */
function getMtcdSeverityRank(conflictType: number): number {
	// 3 = severe, 4 = potential
	if (conflictType === 3) {
		return 2;
	}
	if (conflictType === 4) {
		return 1;
	}
	return 0;
}

/**
 * Returns true when candidate should replace current for display.
 * Priority: severity desc, then earliest conflict time, then lowest conflict id.
 */
function shouldReplaceMtcdDisplayConflict(
	current: {
		conflictType: number;
		conflictingFlightPosition?: { time?: { seconds: bigint | number } };
		id: bigint | number;
	},
	candidate: {
		conflictType: number;
		conflictingFlightPosition?: { time?: { seconds: bigint | number } };
		id: bigint | number;
	},
): boolean {
	const currentSeverity = getMtcdSeverityRank(current.conflictType);
	const candidateSeverity = getMtcdSeverityRank(candidate.conflictType);
	if (candidateSeverity !== currentSeverity) {
		return candidateSeverity > currentSeverity;
	}

	const currentTime = Number(
		current.conflictingFlightPosition?.time?.seconds ?? 0,
	);
	const candidateTime = Number(
		candidate.conflictingFlightPosition?.time?.seconds ?? 0,
	);
	if (candidateTime !== currentTime) {
		return candidateTime < currentTime;
	}

	return Number(candidate.id) < Number(current.id);
}

// ============================================================================
// Main Component
// ============================================================================

export default observer(function Agenda({
	events = [],
}: {
	events?: TimelineEvent[];
}) {
	const posthog = usePostHog();
	const scaleMinutes = cwpStore.agendaScaleMinutes as ScalePreset;
	const [containerHeight, setContainerHeight] = useState(600);
	const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
	const timelineContainerRef = useRef<HTMLDivElement | null>(null);
	// Track whether we should animate positions (only on scale change, not on drag)
	const [animatePosition, setAnimatePosition] = useState(true);
	const prevScaleRef = useRef(scaleMinutes);
	// Track if any card is being dragged (to disable hover on other cards)
	const [isDraggingAny, setIsDraggingAny] = useState(false);

	const currentTimestamp = simulatorStore.timestamp;
	const { mtcdConflictIds } = aircraftStore;
	const radarVisibleAircraftIds = configurationStore.radarVisibleAircraftIds;

	// Enable animation only when scale changes
	useEffect(() => {
		if (prevScaleRef.current !== scaleMinutes) {
			setAnimatePosition(true);
			prevScaleRef.current = scaleMinutes;
		}
	}, [scaleMinutes]);

	// React to external scale requests (e.g., when a new datablock is created)
	useEffect(() => {
		const disposer = reaction(
			() => cwpStore.requestedAgendaScaleMinutes,
			(requestedMinutes) => {
				if (requestedMinutes !== null) {
					// Find the smallest scale preset that includes this time
					const requiredScale = SCALE_PRESETS.find(
						(preset) => preset >= requestedMinutes,
					);
					if (requiredScale && requiredScale > scaleMinutes) {
						cwpStore.setAgendaScaleMinutes(requiredScale);
					}
					cwpStore.clearRequestedAgendaScale();
				}
			},
		);

		return () => {
			disposer();
		};
	}, [scaleMinutes]);

	// Measure container height
	useEffect(() => {
		if (!containerRef) {
			return;
		}
		const updateHeight = () => {
			setContainerHeight(containerRef.clientHeight);
		};
		updateHeight();
		const resizeObserver = new ResizeObserver(updateHeight);
		resizeObserver.observe(containerRef);
		return () => resizeObserver.disconnect();
	}, [containerRef]);

	// Handle mouse wheel zoom with non-passive listener to allow preventDefault
	// Uses threshold-based accumulation to smooth trackpad scrolling while
	// allowing quick mouse wheel notches
	useEffect(() => {
		const container = timelineContainerRef.current;
		if (!container) {
			return;
		}

		let accumulatedDelta = 0;
		let resetTimeout: ReturnType<typeof setTimeout> | null = null;
		const THRESHOLD = 30; // Mouse wheel notches are ~100-120, trackpad events are small
		const RESET_TIMEOUT_MS = 150; // Reset accumulated delta after pause

		const handleWheel = (e: WheelEvent) => {
			e.preventDefault();

			// Clear any existing reset timeout
			if (resetTimeout) {
				clearTimeout(resetTimeout);
			}

			// Accumulate the delta
			accumulatedDelta += e.deltaY;

			// Check if we've accumulated enough to trigger a scale change
			if (Math.abs(accumulatedDelta) >= THRESHOLD) {
				const direction = accumulatedDelta > 0 ? 1 : -1;
				// Reset accumulator after triggering
				accumulatedDelta = 0;
				const currentIndex = SCALE_PRESETS.indexOf(scaleMinutes);
				const newScale =
					direction > 0
						? SCALE_PRESETS[
								Math.min(currentIndex + 1, SCALE_PRESETS.length - 1)
							]
						: SCALE_PRESETS[Math.max(currentIndex - 1, 0)];

				if (newScale !== scaleMinutes) {
					posthog.capture("agenda_scale_changed", {
						previous_scale: scaleMinutes,
						new_scale: newScale,
						method: "wheel",
					});
					cwpStore.setAgendaScaleMinutes(newScale);
				}
			}

			// Reset accumulator after a pause in scrolling
			resetTimeout = setTimeout(() => {
				accumulatedDelta = 0;
			}, RESET_TIMEOUT_MS);
		};

		container.addEventListener("wheel", handleWheel, { passive: false });
		return () => {
			container.removeEventListener("wheel", handleWheel);
			if (resetTimeout) {
				clearTimeout(resetTimeout);
			}
		};
	}, [posthog, scaleMinutes]);

	// Pixels per minute based on container height (minus padding) and scale
	const effectiveHeight = containerHeight - BOTTOM_PADDING_PX;
	const pxPerMinute = effectiveHeight / scaleMinutes;

	// Convert MTCD conflicts to timeline events
	// Display-only de-duplication by aircraft pair: keep only highest severity
	const mtcdConflictsByPair = new Map<
		string,
		typeof mtcdConflictIds extends Map<unknown, infer T> ? T : never
	>();

	for (const conflict of mtcdConflictIds.values()) {
		if (!conflict.flightId || !conflict.conflictingFlightId) {
			continue;
		}

		if (
			!radarVisibleAircraftIds.has(conflict.flightId) ||
			!radarVisibleAircraftIds.has(conflict.conflictingFlightId)
		) {
			continue;
		}

		const pairKey = createAgendaPairKey(
			conflict.flightId,
			conflict.conflictingFlightId,
		);
		const existing = mtcdConflictsByPair.get(pairKey);

		if (!existing || shouldReplaceMtcdDisplayConflict(existing, conflict)) {
			mtcdConflictsByPair.set(pairKey, conflict);
		}
	}

	const mtcdEvents: TimelineEvent[] = [...mtcdConflictsByPair.entries()]
		.filter(([, conflict]) => conflict.flightId && conflict.conflictingFlightId)
		.map(([pairKey, conflict], index) => {
			// Use actual conflict time if available, otherwise use placeholder
			const conflictTime = conflict.conflictingFlightPosition?.time;
			const conflictTimestamp = conflictTime
				? Number(conflictTime.seconds)
				: currentTimestamp + (index + 1) * PLACEHOLDER_CONFLICT_INTERVAL_SEC;

			const minutesFromNow = (conflictTimestamp - currentTimestamp) / 60;

			// Determine severity based on conflictType:
			// 3 = MTCDInputSevere (severe), 4 = MTCDInputPotential (potential)
			const severity =
				conflict.conflictType === 4
					? ("potential" as const)
					: ("severe" as const);

			return {
				id: `mtcd-${pairKey}`,
				startMin: minutesFromNow,
				code:
					conflict.conflictingFlightPosition?.altitude === undefined
						? undefined
						: String(
								convertMetersToFlightLevel(
									conflict.conflictingFlightPosition.altitude,
								),
							),
				labels: [conflict.callSign, conflict.conflictingFlightCallSign],
				aircraftIds: [conflict.flightId, conflict.conflictingFlightId],
				severity,
			};
		});

	// Convert custom datablocks to timeline events
	const customDatablockEvents: TimelineEvent[] = datablockStore.allDatablocks
		.filter((db) =>
			db.aircraftIds.every((aircraftId) =>
				radarVisibleAircraftIds.has(aircraftId),
			),
		)
		.map((db) => {
			// Get callsigns for the labels
			const labels = db.aircraftIds
				.map((id) => aircraftStore.aircrafts.get(id)?.callSign ?? id)
				.slice(0, 2); // Limit to 2 labels

			// Format separation distance for badge (convert NM to feet, format compactly)
			const code =
				db.closestSeparationNM === null
					? undefined
					: formatFeetCompact(convertNMToFeet(db.closestSeparationNM));

			return {
				id: db.id,
				startMin: db.startMin,
				code, // Show closest separation distance
				labels,
				aircraftIds: db.aircraftIds,
				severity: undefined, // No severity for custom datablocks
				eventType: "custom-datablock" as const,
			};
		});

	const allEvents = [...events, ...mtcdEvents, ...customDatablockEvents];

	// Handle range slider change
	const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const index = Number.parseInt(e.target.value, 10);
		const newScale = SCALE_PRESETS[index];
		if (newScale !== scaleMinutes) {
			posthog.capture("agenda_scale_changed", {
				previous_scale: scaleMinutes,
				new_scale: newScale,
				method: "slider",
			});
		}
		cwpStore.setAgendaScaleMinutes(newScale);
	};

	// Handle time offset changes from dragging events
	const handleTimeOffsetChange = useCallback(
		(eventId: string, offsetMin: number) => {
			// Disable animation when dragging completes to prevent jarring movement
			setAnimatePosition(false);
			cwpStore.setAgendaEventTimeOffset(eventId, offsetMin);
		},
		[],
	);

	// Calculate separator lines with smooth scrolling
	// Separators are positioned relative to "now" (exact current time)
	const separatorInterval = getSeparatorInterval(scaleMinutes);
	const separators: Array<{
		bottomPx: number;
		time: string;
		minuteKey: number;
	}> = [];

	// Current time in minutes (floating point for sub-minute precision)
	const currentTimeMinutes = currentTimestamp / 60;
	// Round down to the nearest interval to find the base separator
	const baseMinute =
		Math.floor(currentTimeMinutes / separatorInterval) * separatorInterval;
	// How far past the last interval mark we are (causes the "scrolling" effect)
	const offsetFromBase = currentTimeMinutes - baseMinute;

	// Generate separators, starting from the one at or before "now"
	// We go one interval before and after the visible range to ensure smooth transitions
	for (
		let i = -separatorInterval;
		i <= scaleMinutes + separatorInterval;
		i += separatorInterval
	) {
		const absoluteMinute = baseMinute + i;
		// Position relative to "now": subtract the offset so separators slide down over time
		const minutesFromNow = i - offsetFromBase;
		const bottomPx = minutesFromNow * pxPerMinute + BOTTOM_PADDING_PX;

		// Only include separators within the visible area
		if (bottomPx >= 0 && bottomPx <= containerHeight) {
			separators.push({
				bottomPx,
				time: formatSimulatorTimeHM(absoluteMinute * 60),
				minuteKey: absoluteMinute,
			});
		}
	}

	// Prepare event positions in pixels (filter events within time range)
	// Apply time offsets from cwpStore
	const eventPositions: PositionedEvent[] = allEvents
		.map((ev) => {
			// Apply the stored time offset
			const timeOffset = cwpStore.getAgendaEventTimeOffset(ev.id);
			const adjustedStartMin = ev.startMin + timeOffset;
			return { ...ev, startMin: adjustedStartMin };
		})
		.filter((ev) => ev.startMin >= 0 && ev.startMin <= scaleMinutes)
		.map((ev) => {
			// Position from bottom in pixels, with bottom padding offset
			const bottomPx = ev.startMin * pxPerMinute + BOTTOM_PADDING_PX;
			// Store original position for accurate time offset calculation during drag
			return { ...ev, bottomPx, originalBottomPx: bottomPx };
		});

	// Stack events to avoid overlaps
	const stackedEvents = stackEvents(eventPositions);

	// Filter out events that are pushed outside the visible area after stacking
	const visibleEvents = stackedEvents.filter(
		(ev) => ev.bottomPx >= -EVENT_HEIGHT_PX && ev.bottomPx < containerHeight,
	);

	return (
		<div className="w-44 h-screen bg-base-300 text-base-content border-l-[1.5px] border-base-200 z-40 flex flex-col font-mono">
			{/* Header */}
			<div className="px-2 py-1 border-b border-base-200 shrink-0 bg-neutral-800">
				<h2 className="text-xs font-semibold text-base-content/80">Agenda</h2>
			</div>

			{/* Scale slider control */}
			<div className="p-2 border-b border-base-200 flex items-center gap-2 shrink-0 bg-[#333]">
				<span className="text-[10px] text-base-content/70 w-[4ch] text-right">
					{scaleMinutes}m
				</span>
				<input
					type="range"
					min="0"
					max={SCALE_PRESETS.length - 1}
					value={SCALE_PRESETS.indexOf(scaleMinutes)}
					onChange={handleRangeChange}
					className="range range-xs flex-1 [--range-fill:0] [--range-bg:#666] text-primary"
				/>
			</div>

			{/* Timeline container - fills remaining height */}
			<div
				ref={(el) => {
					setContainerRef(el);
					timelineContainerRef.current = el;
				}}
				className="relative flex-1 overflow-hidden"
			>
				{/* Separator lines with time labels */}
				{separators.map(({ bottomPx, time, minuteKey }) => (
					<TimelineSeparator key={minuteKey} bottomPx={bottomPx} time={time} />
				))}

				{/* Events with fixed height, positioned absolutely */}
				{visibleEvents.map((ev) => (
					<TimelineEventCard
						key={ev.id}
						event={ev}
						timeOffsetMin={cwpStore.getAgendaEventTimeOffset(ev.id)}
						pxPerMinute={pxPerMinute}
						onTimeOffsetChange={handleTimeOffsetChange}
						animatePosition={animatePosition}
						isDraggingAny={isDraggingAny}
						setIsDraggingAny={setIsDraggingAny}
					/>
				))}
			</div>
		</div>
	);
});

// Re-export types for external use
export type { TimelineEvent } from "./types";
